import { useCallback, useEffect, useRef, useState } from "react";
import { loadDirectorySearch } from "../lib/directoryLoad";
import { SEARCH_DEBOUNCE_MS, searchKey, searchTerm } from "../lib/directorySearch";

/**
 * Issue #644 — THE SEARCH, ASKED OF THE SERVER. Wave 5 of epic #609.
 *
 * ## Why this is a hook file and not part of `GroundsMap.jsx`
 *
 * The same two reasons `useDirectoryViewport` gives, and neither is taste.
 *
 * The BUDGET: `GroundsMap.jsx`'s component function is measured against
 * `max-lines-per-function: 710` (`frontend/eslint.config.mjs`, whose own
 * header records that a number is never raised to make a new function fit).
 * #617 hit that wall and answered it by extracting `DirectoryLegend`; #643
 * hit it and extracted the viewport hook. This is the third, and the
 * alternative was not "leave it inline", it was "exceed the budget".
 *
 * The SEAM: every DECISION is one module further out, in
 * `lib/directorySearch.js`, where an ordinary test can call it — the request
 * shape, the page arithmetic, the count line, the scope words. What is left
 * here is state and sequencing: a debounce, a page number, and the rule that
 * a late answer never overwrites a newer one. That part genuinely needs
 * React, and no unit test in this repo can reach it, because nothing here
 * mounts components. Keeping the two apart is the rule #615 and #617 were
 * both bitten by.
 *
 * ## What it does
 *
 * 1. **A term is debounced, a page is not.** Typing is a stream of
 *    half-questions and `SEARCH_DEBOUNCE_MS` waits for it to settle. Pressing
 *    "next page" is one deliberate press, and delaying it by a quarter of a
 *    second for symmetry would only make the button feel broken.
 * 2. **Any change to the term resets to page 1** — #603's rule. Without it,
 *    narrowing a search while on page 3 leaves a reader staring at an empty
 *    page indistinguishable from "nothing matches".
 * 3. **A late answer never overwrites a newer one.** Typing "eden" issues at
 *    most one request, but a slow one for "ede" can still be in flight;
 *    `activeKey` is checked when each lands, so the answer the reader is
 *    waiting for wins regardless of which returns first. Same rule, same
 *    shape, as the viewport hook.
 * 4. **A failure changes nothing but the flag.** #608/#633:
 *    `loadDirectorySearch` returns NO `grounds` key on rejection and this
 *    hook writes no rows on `ok: false`, so a failed search cannot be
 *    rendered as "No matches" — which is a claim about the directory, not
 *    about the request.
 *
 * 5. **A question with no answer yet is not an answer.** `pending` is true
 *    from the keystroke until the answer for THAT term lands. Without it the
 *    first search on a page would render "No matches" for a quarter of a
 *    second — over a directory that does match — which is a WRONG answer
 *    rather than a slow one, and the exact class (#608, #617, #612) this epic
 *    keeps closing. The rows already on screen are kept meanwhile rather than
 *    blanked, for #633's reason.
 *
 * ## What it deliberately does NOT do
 *
 * No cache. The viewport hook caches because panning back to a box you just
 * left is the common gesture and the answer cannot have changed in between;
 * typing back to a term you just left is not, and a search cached across a
 * ground being added or corrected on the same page would answer from before
 * the write. One bounded cache on this page is enough (`mapViewport.js`
 * carries the argument for why an unbounded one is this epic's own bug moved
 * into the browser).
 */

/** Nothing asked yet. `answer` is null for "no search is active". */
const EMPTY_SEARCH = {
  rows: [],
  answer: null,
  searchError: false,
  /** The term the rows and the answer above actually describe. */
  answeredTerm: "",
};

/**
 * `isAdmin` (#683) decides whether the request carries `include_archived=1`
 * — see `directoryLoad.js`'s `directoryBasePath`. Not part of any cache
 * key here because this hook keeps none (see the module header); it is
 * simply passed straight through to `loadDirectorySearch` on every ask.
 */
export function useDirectorySearch(api, rawTerm, isAdmin) {
  const term = searchTerm(rawTerm);
  const [page, setPage] = useState(1);
  const [state, setState] = useState(EMPTY_SEARCH);
  /**
   * #608's retry, bumped: a nonce rather than a flag, because a SECOND retry
   * of the same failed term has to re-ask, and a boolean that is already true
   * would be no change at all.
   */
  const [attempt, setAttempt] = useState(0);
  /** The question the page is currently showing an answer for. */
  const activeKey = useRef(null);

  /**
   * The term changed, so the page number is stale — #603's page-1 reset,
   * applied where the term actually changes rather than in every handler
   * that can change it.
   */
  const termRef = useRef(term);
  if (termRef.current !== term) {
    termRef.current = term;
    if (page !== 1) setPage(1);
  }

  useEffect(() => {
    // An empty box is not a search. Clear the answer so the page falls
    // straight back to the viewport's rows — and clear the key too, or
    // re-typing the same term would be deduplicated into silence.
    if (!term) {
      activeKey.current = null;
      setState(EMPTY_SEARCH);
      return undefined;
    }
    const key = searchKey(term, page);
    activeKey.current = key;
    const timer = setTimeout(() => {
      loadDirectorySearch(api, term, page, isAdmin).then((outcome) => {
        // A newer question has been asked since: this answer describes a
        // term the reader has already moved on from.
        if (activeKey.current !== key) return;
        if (!outcome.ok) {
          // #633's rule: the ONLY thing written is the flag. The rows on
          // screen stay, and the page says the request failed rather than
          // reporting an emptiness nobody measured. `answeredTerm` is set
          // too, so the page stops waiting — a failure IS an outcome, and
          // leaving it pending forever would hide it behind a spinner.
          setState((prev) => ({ ...prev, searchError: true, answeredTerm: term }));
          return;
        }
        setState({
          rows: outcome.grounds,
          answer: {
            matching: outcome.matching,
            page: outcome.page,
            pageCount: outcome.pageCount,
            pageSize: outcome.pageSize,
          },
          searchError: false,
          answeredTerm: term,
        });
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `attempt` is in the list so that Retry re-runs this effect for a term
    // and page that have not changed — see `retry` below.
  }, [api, term, page, attempt, isAdmin]);

  /**
   * Move to another page of the SAME search. Clamped by the caller against
   * the answer's own `page_count` (`clampPage`), so this takes a number and
   * does not need to know how many there are.
   */
  const goToPage = useCallback((next) => {
    setPage(Math.max(1, Math.trunc(Number(next)) || 1));
  }, []);

  /**
   * #608's retry, for the search. A failed search MUST have a remedy on
   * screen — that issue's whole finding was that a failure with no way to
   * try again is indistinguishable from a product that is broken — and the
   * remedy is a retry, never "add a ground".
   *
   * It clears the flag as well as re-asking, so the failure state resolves on
   * success rather than persisting behind a good answer.
   */
  const retry = useCallback(() => {
    setState((prev) => ({ ...prev, searchError: false, answeredTerm: "" }));
    setAttempt((n) => n + 1);
  }, []);

  /**
   * #1143 — ask again for the SAME term and page, after a ground was added
   * that the last answer cannot hold. Unlike `retry` it clears nothing: the
   * rows on screen stay until the new answer lands, so the list does not
   * flicker to "searching" under the reader. With no term it is a no-op (the
   * effect clears the answer and asks nothing).
   */
  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  /**
   * A question asked but not yet answered. See rule 5 above: this is what
   * stops a page reporting "No matches" in the gap before its own search has
   * come back.
   */
  const pending = Boolean(term) && state.answeredTerm !== term;

  return { ...state, term, page, pending, goToPage, setPage, retry, refresh };
}
